import React, {useState,useEffect,useRef} from 'react';
import Toast from './toast';
import bBotRemaps from '@manta-scouting/shared-assets/bbot-remaps.json';
const TBA_BASE = "https://www.thebluealliance.com/api/v3";
const TBA_API_KEY = "UXPQHaOF7xaeYl5uMiI7p7ZzGCYuneCwADJLNzfgVerzaEiONryKU0k9NXbFUAyy"

const TBA_SCHEDULE_CACHE_PREFIX = "tba_schedule_cache_";

/**
 * Why the most recent fetchTBAData call failed: "notFound" (404), "network" (offline/other error),
 * or null (it succeeded, possibly with an empty JSON null body).
 */
let lastTBAError = null;

/**
 * Caches a fetched match schedule (a Map of matchKey -> Map of alliance data) per event/kind
 * in localStorage, so it can be read back offline if a later fetch fails.
 * @param {string} eventPath
 * @param {"quals"|"elims"} kind
 * @param {Map} matchMap
 */
function cacheMatchSchedule(eventPath, kind, matchMap) {
    try {
        const serialized = JSON.stringify([...matchMap].map(([key, value]) =>
            [key, value instanceof Map ? [...value] : value]
        ));
        localStorage.setItem(TBA_SCHEDULE_CACHE_PREFIX + kind + "_" + eventPath, serialized);
    } catch (err) {
        console.warn("Failed to cache TBA schedule:", err);
    }
}

/**
 * Reads a previously-cached match schedule for an event/kind, if there is one.
 * @param {string} eventPath
 * @param {"quals"|"elims"} kind
 * @returns {Map|null}
 */
function loadCachedMatchSchedule(eventPath, kind) {
    try {
        const raw = localStorage.getItem(TBA_SCHEDULE_CACHE_PREFIX + kind + "_" + eventPath);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return new Map(parsed.map(([key, value]) => [key, new Map(value)]));
    } catch (err) {
        console.warn("Failed to load cached TBA schedule:", err);
        return null;
    }
}

/**
 * B-bots: a team's second robot shows on the TBA schedule as "frc7415B", and in the team list as an
 * Off-Season Demo Team (frc9975). The event's remap_teams links them; scouted data uses the demo number
 * (Neptune names it "<7415's name> (B-Bot)"). Without this, the B-bot was recorded as 7415 itself.
 */
let bBotKeyToNumber = new Map();
const TBA_REMAP_CACHE_PREFIX = "tba_remap_cache_";

/** TBA team key → scouted team number ("frc254" → 254, "frc7415B" → 9975 when the event remaps it). */
export function tbaTeamNumber(teamKey) {
    const key = String(teamKey ?? "").trim().toLowerCase();
    if (bBotKeyToNumber.has(key)) return bBotKeyToNumber.get(key);
    return parseInt(key.match(/\d+/));
}

/** A B-bot's TBA name for its demo number (9974 → "4414B"), or null for an ordinary team. */
export function bBotLabel(teamNumber) {
    const n = parseInt(teamNumber);
    for (const [key, demo] of bBotKeyToNumber) {
        if (demo === n) return key.replace(/^frc/, "").toUpperCase();
    }
    return null;
}

/** Load the event's B-bot pairs (cached for offline). Returns Map demoNumber → mainNumber. */
async function loadBBotRemap(eventPath) {
    let remap = null;
    const fetched = await fetchTBAData("https://www.thebluealliance.com/api/v3/event/" + eventPath + "?X-TBA-Auth-Key=" + TBA_API_KEY);
    if (fetched !== -1 && fetched) {
        remap = fetched.remap_teams || {};
        try { localStorage.setItem(TBA_REMAP_CACHE_PREFIX + eventPath, JSON.stringify(remap)); } catch { /* optional */ }
    } else {
        try { remap = JSON.parse(localStorage.getItem(TBA_REMAP_CACHE_PREFIX + eventPath) || "null"); } catch { remap = null; }
    }
    const keyMap = new Map();
    const demoToMain = new Map();
    // Pairs the event sent before TBA had them (packages/shared-assets/bbot-remaps.json); TBA wins
    remap = { ...(bBotRemaps[eventPath] || {}), ...(remap || {}) };
    for (const [demoKey, bKey] of Object.entries(remap)) {
        const demo = parseInt(String(demoKey).match(/\d+/));
        const lettered = String(bKey).toLowerCase().match(/^frc(\d+)([a-z])$/);
        if (!demo || !lettered) continue;
        keyMap.set(String(bKey).toLowerCase(), demo);
        demoToMain.set(demo, parseInt(lettered[1]));
    }
    bBotKeyToNumber = keyMap;
    return demoToMain;
}

export const useTBA = (gamemode) => {
    const blank = "- - - -"
    // The offline copy starts on the event it was downloaded with (lib/offlineDownload.js)
    const [eventPath, setEventPath] = useState(() => (typeof window !== 'undefined' && window.__OSPREY_EVENT__) || '');
    const [tbaEventOverride, setTbaEventOverrideState] = useState(() => {
        try {
            return localStorage.getItem("tbaEventOverride") || "";
        } catch {
            return "";
        }
    });
    const [tbaOnline, setTbaOnline] = useState(false);
    /**
     * "none" (no path), "found" (schedule loaded), "noSchedule" (event exists but no matches posted yet),
     * "notFound" (TBA has no such event), or "offline" (couldn't reach TBA and nothing cached)
     */
    const [tbaStatus, setTbaStatus] = useState("none");
    const [teamList, setTeamList] = useState(null);
    const [refreshToken, setRefreshToken] = useState(0);
    const [robotNameList, setRobotNameList] = useState(null)

    const refresh = () => setRefreshToken(prev => prev + 1);

    /**
     * Persists the override so a scout doesn't have to re-enter it every time the app reloads.
     * Also remembers which event it was set for, so switching events clears it (below).
     */
    const setTbaEventOverride = (value) => {
        setTbaEventOverrideState(value);
        try {
            localStorage.setItem("tbaEventOverride", value);
            if (value) localStorage.setItem("tbaEventOverrideFor", eventPath);
            else localStorage.removeItem("tbaEventOverrideFor");
        } catch {
            // ignore storage errors (e.g. private browsing)
        }
    };

    // An override only applies to the event it was set on. Reloading the same event keeps it;
    // switching to a different event clears it so TBA follows the new event again.
    useEffect(() => {
        if (!eventPath || eventPath === blank) return;
        let overrideFor = null;
        try {
            overrideFor = localStorage.getItem("tbaEventOverrideFor");
        } catch {
            return;
        }
        if (!tbaEventOverride) return;
        if (!overrideFor) {
            // Override saved before this was tracked: pin it to the event it's being used with.
            try { localStorage.setItem("tbaEventOverrideFor", eventPath); } catch { /* ignore */ }
            return;
        }
        if (overrideFor !== eventPath) {
            setTbaEventOverride("");
            Toast.info("TBA event override cleared for the new event");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventPath]);

    // The TBA event key actually queried. When an override is set (Settings > TBA Event), TBA
    // lookups (team list, matches) use it instead of eventPath, while eventPath itself keeps
    // pointing at wherever scouted data actually gets written in Firebase.
    const tbaEventPath = tbaEventOverride ? tbaEventOverride.trim() : eventPath;

    /**
     * Sets the teamList of matches/robots based on the entered tba path
     * Updates whenever the path changes
     */
    useEffect(() => {
        let cancelled = false;
        const fetchTBA = async () => {
            if (tbaEventPath === blank || !tbaEventPath) {
                setTbaOnline(false);
                setTbaStatus("none");
                setTeamList(null);
                setRobotNameList(null);
                return;
            }

            console.log("Updating TBA: " + tbaEventPath);
            // B-bot pairs before the schedule, so "frc7415B" autofills its demo number (9975)
            const bBotMains = await loadBBotRemap(tbaEventPath);
            let matches;
            if (gamemode !== 3) {
                matches = await getTBAMatches(tbaEventPath);
            } else {
                matches = await getTBAElimsMatches(tbaEventPath);
            }
            const matchesError = lastTBAError;
            const robots = await getTBAEventRobots(tbaEventPath, bBotMains);
            if (cancelled) return; // a newer path/refresh superseded this one; let it do the toasting
            setTeamList(matches);
            setRobotNameList(robots);

            if (matches == null) {
                setTbaOnline(false);
                if (matchesError === "notFound") {
                    setTbaStatus("notFound");
                    Toast.error(`TBA event "${tbaEventPath}" not found`);
                } else {
                    setTbaStatus("offline");
                    Toast.error("Couldn't reach TBA - team numbers won't autofill");
                }
            } else if (matches.size === 0) {
                // The event exists, but TBA hasn't posted a schedule yet (common before an event starts).
                // Not "online": autofill would otherwise blank out team numbers scouts type in.
                setTbaOnline(false);
                setTbaStatus("noSchedule");
                Toast.warning(`TBA event found, but no ${gamemode === 3 ? "elims" : "match"} schedule is posted yet - enter team numbers manually`);
            } else {
                setTbaOnline(true);
                setTbaStatus("found");
                Toast.info("TBA path found");
            }
        };
        fetchTBA();
        return () => { cancelled = true; };
    }, [tbaEventPath, refreshToken, gamemode]);

    return { eventPath, setEventPath, teamList, tbaOnline, tbaStatus, refresh, robotNameList, tbaEventOverride, setTbaEventOverride, tbaEventPath };
};

/**
 * Gets the robot number of a speciic match, position, and color
 * @param {int} matchNum the match number
 * @param {int} robPos either 1,2,or3
 * @param {string} robColor either r or b
 * @param {*} teamListMap the teamList from useTBA
 * @returns {int} robot number
 */
export function getRobotFromMatch(matchNum, robPos, robColor, teamListMap){
    if(!teamListMap.has("match_" + String(matchNum))) return null;
    let match = teamListMap.get("match_" + String(matchNum));
    let robots;
    if(robColor.toLowerCase() == "r" || robColor.toLowerCase() == "red"){
        robots = match.get("red");
    }
    else{
        robots = match.get("blue");
    }
    let theRobt = robots[parseInt(robPos)-1];
    return theRobt;
}

/**
 * Gets all matches' alliances teams/positions through tba api
 * @param {*} eventPath 
 * @returns 
 */
async function getTBAMatches(eventPath) {
    let tbaApiUrl =
        "https://www.thebluealliance.com/api/v3/event/" +
        eventPath +
        "/matches?X-TBA-Auth-Key="+TBA_API_KEY;
    const fetchedData = await fetchTBAData(tbaApiUrl);
    if (fetchedData == -1) {
        const cached = loadCachedMatchSchedule(eventPath, "quals");
        if (cached) {
            Toast.info("Offline - using cached quals schedule");
            return cached;
        }
        return null;
    }
    let tempMapOfData = new Map();
    for(const match of fetchedData){
        if(match['comp_level'] != "qm") continue; //only for qual matches
        let redAlliance = match["alliances"]["red"]["team_keys"];
        let blueAlliance = match["alliances"]["blue"]["team_keys"];
        let matchMap = new Map();

        redAlliance.forEach((team,i) => {
            redAlliance[i] = tbaTeamNumber(team);
        });
        blueAlliance.forEach((team,i) => {
            blueAlliance[i] = tbaTeamNumber(team);
        });


        matchMap.set("red", redAlliance);
        matchMap.set("blue", blueAlliance);
        let matchnumber = "match_" + match["match_number"]
        tempMapOfData.set(matchnumber, matchMap)
    }
    //console.log("The match data map: ", tempMapOfData);
    cacheMatchSchedule(eventPath, "quals", tempMapOfData);
    return tempMapOfData;


}

async function getTBAElimsMatches(eventPath) {
    const matchesUrl = "https://www.thebluealliance.com/api/v3/event/" + eventPath + "/matches?X-TBA-Auth-Key=" + TBA_API_KEY;
    const fetchedData = await fetchTBAData(matchesUrl);
    //console.log("fetchedData:", fetchedData);
    if (fetchedData == -1) {
        const cached = loadCachedMatchSchedule(eventPath, "elims");
        if (cached) {
            Toast.info("Offline - using cached elims schedule");
            return cached;
        }
        return null;
    }

    const alliances = await getTBAAlliances(eventPath);
    //console.log("alliances:", alliances);
    if (!alliances) {
        const cached = loadCachedMatchSchedule(eventPath, "elims");
        if (cached) {
            Toast.info("Offline - using cached elims schedule");
            return cached;
        }
        // The matches request just succeeded, so TBA is reachable: no alliances means they haven't
        // been selected yet. Report an empty elims schedule rather than "not found".
        if (lastTBAError === null) return new Map();
        return null;
    }
    const elimsMatches = fetchedData
        .filter(m => m['comp_level'] !== "qm")
        .sort((a,b) => parseInt((a['comp_level']==("sf") ? "0" : "10") + a['key'].split("_")[1].split("m1")[0].split("f")[1])-parseInt((b['comp_level']==("sf") ? "0" : "10") + b['key'].split("_")[1].split("m1")[0].split("f")[1]));
    console.log(elimsMatches);
    //console.log("elimsMatches count:", elimsMatches.length);

    const findAllianceNum = (teams) => {
        for (let i = 0; i < alliances.length; i++) {
            const allianceTeams = alliances[i].picks.map(t => tbaTeamNumber(t));
            if (allianceTeams.includes(teams[0]) || allianceTeams.includes(teams[1]) || allianceTeams.includes(teams[2])) return i + 1;
        }
        console.warn("no alliance found for teams:", teams);
        return null;
    };

    let tempMapOfData = new Map();
    elimsMatches.forEach((match, index) => {
        let redTeams = match["alliances"]["red"]["team_keys"].map(t => tbaTeamNumber(t));
        let blueTeams = match["alliances"]["blue"]["team_keys"].map(t => tbaTeamNumber(t));

        const redAllianceNum = findAllianceNum(redTeams);
        const blueAllianceNum = findAllianceNum(blueTeams);

        let matchMap = new Map();
        matchMap.set("red", redTeams);
        matchMap.set("blue", blueTeams);
        matchMap.set("red_alliance", redAllianceNum);
        matchMap.set("blue_alliance", blueAllianceNum);
        matchMap.set("red_picks", redAllianceNum ? alliances[redAllianceNum - 1].picks.map(t => tbaTeamNumber(t)) : redTeams);
        matchMap.set("blue_picks", blueAllianceNum ? alliances[blueAllianceNum - 1].picks.map(t => tbaTeamNumber(t)) : blueTeams);

        //console.log(`match_${index+1}: red_picks=${matchMap.get("red_picks")}, blue_picks=${matchMap.get("blue_picks")}`);
        tempMapOfData.set("match_" + (index + 1), matchMap);
    });

    //console.log("Final elims map:", tempMapOfData);
    cacheMatchSchedule(eventPath, "elims", tempMapOfData);
    return tempMapOfData;
}

export function getRobotFromElimsMatch(matchKey, robPos, robColor, teamListMap) {
    if (!teamListMap || !teamListMap.has(matchKey)) return null;
    let match = teamListMap.get(matchKey);
    const picksKey = (robColor.toLowerCase() === "r" || robColor.toLowerCase() === "red") ? "red_picks" : "blue_picks";
    let robots = match.get(picksKey);
    if (!robots) return null;
    const participatingRobots = match.get((robColor.toLowerCase() === "r" || robColor.toLowerCase() === "red") ? "red" : "blue");
    if (!participatingRobots.includes(robots[parseInt(robPos)-1])) {
        Toast.info("Scouting alternate because specified robot is not playing")
        return robots[3] ?? null;
    }
    if (parseInt(robPos) > robots.length) {
        Toast.error("Full alliance is not present.");
        return null;
    }
    return robots[parseInt(robPos) - 1];
}

async function getTBAAlliances(eventPath) {
    let tbaApiUrl =
        "https://www.thebluealliance.com/api/v3/event/" +
        eventPath +
        "/alliances?X-TBA-Auth-Key=" + TBA_API_KEY;
    const fetchedData = await fetchTBAData(tbaApiUrl);
    if (fetchedData == -1) return null;
    
    // Returns array of alliances, index 0 = alliance 1, index 1 = alliance 2, etc.
    // Each alliance has team_keys: ["frc1234", "frc5678", "frc9012", "frc3333"] (captain + 3 picks)
    return fetchedData;
}

async function getTBAEventRobots(eventPath, bBotMains = new Map()) {
    try {
        let tbaUrl = "https://www.thebluealliance.com/api/v3/event/" +
        eventPath +
        "/teams?X-TBA-Auth-Key=" + 
        TBA_API_KEY;
        const fetchedData = await fetchTBAData(tbaUrl);

        if (fetchedData === -1 || !fetchedData) return null;


        // map: { team_number: nickname }
        const robotMap = new Map();

        fetchedData.forEach(team => {
            robotMap.set(team.team_number, team.nickname)
        });
        // Demo-team B-bots: "<main team's name> (B-Bot)" instead of "Off-Season Demo Team 9975"
        for (const [demo, main] of bBotMains) {
            robotMap.set(demo, `${robotMap.get(main) || `Team ${main}`} (B-Bot)`);
        }
        console.log(robotMap)
        return robotMap;

    } catch (err) {
        console.error("TBA robots fetch failed:", err);
        return null;
    }
}

export async function getTBAMatchVideo(eventPath, matchNumber, gamemode) {
    if (!eventPath || !matchNumber || eventPath === "- - - -") return null;
    let matchKey;
    if (gamemode !== 3) {
        matchKey = `${eventPath}_qm${matchNumber}`;
    } else {
        getTBAElimsMatches(eventPath).then(teamListMap => {
            const matchKey = teamListMap ? Array.from(teamListMap.keys())[matchNumber - 1] : null;
            if (!matchKey) {
                console.warn("No match key found for elims match number:", matchNumber);
                return null;
            }
        });
    }
    
    const tbaApiUrl =
        "https://www.thebluealliance.com/api/v3/match/" +
        matchKey +
        "?X-TBA-Auth-Key=" + TBA_API_KEY;
    const fetchedData = await fetchTBAData(tbaApiUrl);
    if (fetchedData == -1 || !fetchedData) return null;

    const videos = fetchedData.videos;
    if (!videos || videos.length === 0) return null;

    const ytVideo = videos.find(v => v.type === "youtube");
    if (!ytVideo) return null;
    //console.log("Found YouTube video for match:", matchKey, "video key:", ytVideo.key);
    return `https://www.youtube.com/watch?v=${ytVideo.key}`;
}

/**
 * Attempts to fetch data from TBA database, returns null if path is invalid
 * @param {String} apiUrl 
 * @returns 
 */
/**
 * The TBA answers an offline copy was downloaded with (lib/offlineDownload.js), by path
 * ("/event/2026casan1/teams"), or undefined when it has none for this request.
 */
function embeddedTBA(apiUrl) {
    const snapshot = typeof window !== 'undefined' ? window.__OSPREY_TBA__ : undefined;
    if (!snapshot) return undefined;
    return snapshot[String(apiUrl).replace(TBA_BASE, '').split('?')[0]];
}

/** The TBA answers the offline copy embeds for an event: the event, its teams, schedule and alliances. */
export async function tbaEventSnapshot(eventKey) {
    const snapshot = {};
    for (const path of [`/event/${eventKey}`, `/event/${eventKey}/teams`, `/event/${eventKey}/matches`, `/event/${eventKey}/alliances`]) {
        const data = await fetchTBAData(TBA_BASE + path + "?X-TBA-Auth-Key=" + TBA_API_KEY);
        if (data !== -1) snapshot[path] = data;
    }
    return snapshot;
}

async function fetchTBAData(apiUrl) {
    lastTBAError = null;
    try {
        const response = await fetch(apiUrl);
        if (response.status === 404) {
            lastTBAError = "notFound";
            throw new Error("Event Path not Found on TBA");
        }
        if (!response.ok) {
            lastTBAError = "network";
            throw new Error("TBA request failed with status " + response.status);
        }
        var allData = await response.json();
        allData = JSON.parse(JSON.stringify(allData));
        // A valid-but-empty resource (e.g. alliances before selection) comes back as JSON null.
        return allData != null ? allData : -1;
    } catch (error) {
        if (lastTBAError === null) lastTBAError = "network";
        // No internet in the offline copy: the answers it was downloaded with, if it has this one
        const embedded = lastTBAError === "network" ? embeddedTBA(apiUrl) : undefined;
        if (embedded !== undefined) {
            lastTBAError = null;
            return embedded != null ? embedded : -1;
        }
        console.error(error);
        return -1;
    }
};